import React, { useCallback, useMemo, useState } from "react";
import {
  novelBrowserWitnessSourceKey,
  recordNovelBrowserPageEvidenceV3C,
  summarizeNovelBrowserPageEvidenceV3C,
} from "../utils/novelBrowserPageEvidenceV3C";
import CreativePublicationProofViewer from "./CreativePublicationProofViewer";
import ChildrensBookManuscriptReadOnly from "./ChildrensBookManuscriptReadOnly";
import { NovelStorySceneReadOnly } from "./NovelStorySceneManuscript";
import NovelStoryPaginatedInterior from "./NovelStoryPaginatedInterior";
import { hasReadableNovelSavedSourceText } from "../utils/novelProductionSourceManifest";

function clean(value) {
  return String(value ?? "").trim();
}
function safeArray(value) {
  return Array.isArray(value) ? value : [];
}
// Proof warnings must use the same saved production-source text rules
// as Source Readiness and Inventory. This is read-only and never approves print.
export function novelProofChapterHasSavedText(chapter, sceneById) {
  const ids = safeArray(chapter?.sceneIds);
  return ids.length > 0 && ids.every((id) =>
    hasReadableNovelSavedSourceText(sceneById?.get(id))
  );
}

// Match Novel Book Setup's saved enum; never infer a print trim from a browser width.
// Unrecognized/custom formats stay unmeasured until the author specifies dimensions.
export function novelProofTrim(bookFormat = {}) {
  const sizes = {
    "8_x_8_in": [8, 8],
    "8.25_x_8.25_in": [8.25, 8.25],
    "8.5_x_8.5_in": [8.5, 8.5],
    "8_x_10_in": [8, 10],
    "8.5_x_11_in": [8.5, 11],
    "5.5_x_8.5_in": [5.5, 8.5],
    "6_x_9_in": [6, 9],
    a4: [8.27, 11.69],
    a5: [5.83, 8.27],
  };
  const code = clean(bookFormat?.trimSize);
  let found = sizes[code] || null;
  let label = found
    ? `${found[0]} × ${found[1]} inches`
    : "Trim size not selected";
  if (code === "custom_dimensions") {
    const custom = clean(bookFormat?.customTrimSize);
    const match = custom.match(/^\s*(\d+(?:\.\d+)?)\s*(?:x|×|by)\s*(\d+(?:\.\d+)?)\s*(?:in|inches|inch)?\s*$/i);
    const numbers = match ? [Number(match[1]), Number(match[2])] : null;
    found = numbers && numbers.every((value) => value >= 3 && value <= 24)
      ? numbers
      : null;
    label = found ? `${found[0]} × ${found[1]} inches (custom)` : "Custom trim dimensions not confirmed";
  }
  if (found && ["portrait", "landscape"].includes(clean(bookFormat?.orientation))) {
    const orientation = clean(bookFormat.orientation);
    if (orientation === "landscape" && found[0] < found[1]) {
      found = [found[1], found[0]];
    } else if (orientation === "portrait" && found[0] > found[1]) {
      found = [found[1], found[0]];
    }
    label = `${found[0]} × ${found[1]} inches`;
  }
  return {
    measurable: Boolean(found),
    width: found?.[0] || 0,
    height: found?.[1] || 0,
    label,
  };
}

export default function NovelStoryPublicationProof({
  title = "",
  authorName = "",
  bookFormat = {},
  frontCoverUrl = "",
  backCoverUrl = "",
  frontMatter = [],
  chapters = [],
  backMatter = [],
  sceneById,
  unassignedSceneIds = [],
  isFR = false,
  onReturn,
  returnToPreparation = false,
  onOpenBookSetup,
  onOpenChapter,
  onOpenMatter,
}) {
  const [interiorViewMode, setInteriorViewMode] = useState("pages");
  const [browserPageEvidence, setBrowserPageEvidence] = useState({ sourceKey: "", observed: {} });
  const front = safeArray(frontMatter);
  const chaptersSaved = safeArray(chapters);
  const back = safeArray(backMatter);
  const unassigned = safeArray(unassignedSceneIds);
  const trim = novelProofTrim(bookFormat);
  const format = { trim, bleed: clean(bookFormat?.bleed) };
  const interiorItems = [
    ...front.map((item, index) => ({
      id: item.id,
      kind: "front",
      index,
      item,
      pageLabel: `${isFR ? "Préliminaires" : "Front Matter"} ${index + 1} / ${front.length} · ${clean(item.title)}`,
    })),
    ...chaptersSaved.map((chapter, index) => ({
      id: chapter.id,
      kind: "chapter",
      index,
      item: chapter,
      pageLabel: `${isFR ? "Chapitre" : "Chapter"} ${index + 1} / ${chaptersSaved.length} · ${clean(chapter.title)}`,
    })),
    ...back.map((item, index) => ({
      id: item.id,
      kind: "back",
      index,
      item,
      pageLabel: `${isFR ? "Postliminaires" : "Back Matter"} ${index + 1} / ${back.length} · ${clean(item.title)}`,
    })),
  ];

  const witnessSourceKey = useMemo(() => novelBrowserWitnessSourceKey({
    bookFormat, frontMatter: front, chapters: chaptersSaved,
    backMatter: back, sceneById,
  }), [bookFormat, front, chaptersSaved, back, sceneById]);
  const visibleBrowserCounts = browserPageEvidence.sourceKey === witnessSourceKey
    ? browserPageEvidence.observed : {};
  const browserEvidence = summarizeNovelBrowserPageEvidenceV3C({
    sections: interiorItems, observed: visibleBrowserCounts,
  });
  const onBrowserPageMetrics = useCallback((unitKey, metrics) => {
    setBrowserPageEvidence((current) => recordNovelBrowserPageEvidenceV3C(current, {
      sourceKey: witnessSourceKey, unitKey, pageCount: metrics?.pageCount,
    }));
  }, [witnessSourceKey]);

  const issues = [];
  const printRequested = clean(bookFormat?.publicationFormat) !== "digital";
  if (printRequested && !trim.measurable) {
    issues.push({ severity: "blocking", code: "novel_proof_trim_missing" });
  }
  if (chaptersSaved.length === 0) {
    issues.push({ severity: "blocking", code: "novel_proof_chapters_missing" });
  }
  for (const [index, chapter] of chaptersSaved.entries()) {
    if (!novelProofChapterHasSavedText(chapter, sceneById)) {
      issues.push({
        severity: "warning",
        code: "novel_proof_chapter_incomplete",
        targetId: chapter?.id || index,
        chapterLabel: clean(chapter?.title) || `${isFR ? "Chapitre" : "Chapter"} ${index + 1}`,
      });
    }
  }
  if (!clean(frontCoverUrl)) issues.push({ severity: "warning", code: "novel_proof_front_cover_missing" });
  if (!clean(backCoverUrl)) issues.push({ severity: "warning", code: "novel_proof_back_cover_missing" });
  if (unassigned.length > 0) {
    issues.push({ severity: "warning", code: "novel_proof_unassigned", count: unassigned.length });
  }
  issues.push({ severity: "warning", code: "novel_proof_not_paginated" });

  const messages = {
    novel_proof_trim_missing: isFR
      ? "Choisissez un format de coupe mesurable dans Préparer le livre pour l'épreuve imprimée."
      : "Choose a measurable print trim size in Book Setup before relying on the physical frame.",
    novel_proof_chapters_missing: isFR
      ? "Ajoutez au moins un chapitre avant l'épreuve."
      : "Add at least one Chapter before proofing the interior.",
    novel_proof_chapter_incomplete: isFR
      ? "Chapitre incomplet ou scène enregistrée introuvable."
      : "Chapter has missing or empty saved Canon Scenes.",
    novel_proof_front_cover_missing: isFR
      ? "La couverture avant est absente ; un substitut est affiché."
      : "No saved front cover; a placeholder is shown.",
    novel_proof_back_cover_missing: isFR
      ? "La couverture arrière est absente ; un substitut est affiché."
      : "No saved back cover; a placeholder is shown.",
    novel_proof_unassigned: isFR
      ? "Les scènes Canon non affectées ne font pas partie du manuscrit assemblé."
      : "Unassigned Canon Scenes are not part of the assembled manuscript.",
    novel_proof_not_paginated: interiorViewMode === "pages" && trim.measurable
      ? (isFR
          ? "Pages mesurées dans le navigateur, pas encore une pagination imprimée validée, ni un PDF prêt pour l'impression."
          : "Browser-measured preview pages only: final print-file pagination is not verified; no print-ready PDF or export.")
      : (isFR
          ? "Aperçu privé par section : ceci n'est ni une épreuve paginée ni un PDF prêt pour l'impression."
          : "Private section-by-section layout preview only: not final pagination, a print-ready PDF, or an export."),
  };
  function formatIssue(issue) {
    const detail = issue?.code === "novel_proof_chapter_incomplete"
      ? ` · ${clean(issue.chapterLabel)}`
      : issue?.code === "novel_proof_unassigned"
      ? ` · ${Number(issue.count) || 0}`
      : "";
    return `${messages[clean(issue?.code)] || clean(issue?.code)}${detail}`;
  }

  function coverPanel(side, url) {
    const isFront = side === "front";
    return (
      <section
        data-novel-story-proof-cover-side={side}
        aria-label={isFront ? "Front cover proof" : "Back cover proof"}
        style={{
          position: "relative",
          aspectRatio: trim.measurable ? `${trim.width} / ${trim.height}` : "4 / 5",
          border: "1px solid rgba(148,163,184,0.5)",
          borderRadius: 12,
          background: isFront ? "#ecfdf5" : "#fff7ed",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          minHeight: 280,
        }}
      >
        {clean(url) ? (
          <img
            alt={isFront ? "Saved front cover" : "Saved back cover"}
            src={url}
            style={{ display: "block", width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <div style={{ padding: 18, textAlign: "center" }}>
            <div className="ns-card-meta">{isFront ? "FRONT COVER" : "BACK COVER"}</div>
            <h3 style={{ fontFamily: "Georgia, serif" }}>{title}</h3>
            {isFront ? <p>{authorName}</p> : null}
            <p className="ns-card-meta">Saved cover not yet supplied</p>
          </div>
        )}
      </section>
    );
  }

  function renderCover() {
    return (
      <div data-novel-story-publication-cover="true">
        <p className="ns-card-meta" style={{ margin: "0 0 12px", textAlign: "center" }}>
          {isFR ? "Couvertures enregistrées · dos et dimensions finales non calculés" :
            "Saved cover images · spine width and final cover dimensions are not calculated"}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))", gap: 14 }}>
          {coverPanel("back", backCoverUrl)}
          {coverPanel("front", frontCoverUrl)}
        </div>
        {typeof onOpenBookSetup === "function" ? (
          <button className="ns-button ns-button-outline" type="button"
            onClick={onOpenBookSetup} style={{ marginTop: 14 }}>
            {isFR ? "Modifier la source dans Préparer le livre" : "Edit Cover and Format in Book Setup"}
          </button>
        ) : null}
      </div>
    );
  }

  function renderInteriorItem(entry) {
    const item = entry?.item || {};
    const kind = entry?.kind;
    return (
      <article
        data-novel-story-publication-interior={kind}
        style={{ padding: "clamp(14px, 3vw, 34px)", lineHeight: 1.75 }}
      >
        {interiorViewMode === "pages" && trim.measurable ? null : (
          <div
            className="ns-card-meta"
            style={{ textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 900, marginBottom: 12 }}
          >
            {kind === "chapter"
              ? `${isFR ? "CHAPITRE" : "CHAPTER"} ${entry.index + 1}`
              : kind === "front"
              ? (isFR ? "PRÉLIMINAIRES" : "FRONT MATTER")
              : (isFR ? "POSTLIMINAIRES" : "BACK MATTER")}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12, flexWrap: "wrap" }}>
          {interiorViewMode === "pages" && trim.measurable ? null : (
            <h2 style={{ margin: "0 0 18px", fontFamily: "Georgia, serif" }}>
              {clean(item.title) || (isFR ? "Sans titre" : "Untitled")}
            </h2>
          )}
          <button
            className="ns-button ns-button-outline"
            type="button"
            data-novel-story-proof-edit-source={kind}
            onClick={() => {
              if (kind === "chapter") onOpenChapter?.(item.id);
              else onOpenMatter?.(kind, item.id);
            }}
          >
            {isFR ? "Modifier la source" : "Edit Source"}
          </button>
        </div>
        {interiorViewMode === "pages" && trim.measurable ? (
          <NovelStoryPaginatedInterior
            key={`${kind}-${item.id || entry?.index || 0}`}
            entry={entry}
            sceneById={sceneById}
            bookFormat={bookFormat}
            isFR={isFR}
            onBrowserPageMetrics={onBrowserPageMetrics}
          />
        ) : kind === "chapter" ? (
          safeArray(item.sceneIds).length > 0 ? (
            safeArray(item.sceneIds).map((sceneId, sceneIndex) => {
              const scene = sceneById?.get(sceneId);
              return scene ? (
                <section key={sceneId} style={{ margin: "0 0 22px" }}
                  data-novel-story-proof-scene="true">
                  <h3 style={{ fontSize: 14, margin: "0 0 10px", color: "#475569" }}>
                    {clean(scene.title) || `Scene ${sceneIndex + 1}`}
                  </h3>
                  <NovelStorySceneReadOnly scene={scene} lang={isFR ? "fr" : "en"} />
                </section>
              ) : (
                <p key={sceneId} role="alert">
                  {isFR ? "Scène enregistrée introuvable" : "Saved Canon Scene unavailable"} · {sceneIndex + 1}
                </p>
              );
            })
          ) : (
            <p className="ns-card-meta">{isFR ? "Aucune scène affectée" : "No assigned Canon Scenes"}</p>
          )
        ) : (
          <ChildrensBookManuscriptReadOnly
            content={item.manuscriptContent || null}
            fallbackText={String(item.text || "")}
            lang={isFR ? "fr" : "en"}
            presentation="publication_proof"
          />
        )}
      </article>
    );
  }

  return (
    <section data-novel-story-publication-proof="v1" data-novel-story-proof-page-flow-v2="true">
      <div
        data-novel-proof-page-flow-mode="true"
        style={{
          margin: "0 0 16px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          padding: 12,
          borderRadius: 14,
          border: "1px solid rgba(37,99,235,0.20)",
          background: "#eff6ff",
        }}
      >
        <strong>{isFR ? "Affichage intérieur" : "Interior reading mode"}</strong>
        <button
          type="button"
          className={interiorViewMode === "pages" ? "ns-button ns-button-primary" : "ns-button ns-button-outline"}
          data-novel-proof-page-flow-select="pages"
          disabled={!trim.measurable}
          onClick={() => setInteriorViewMode("pages")}
        >
          {isFR ? "Pages mesurées" : "Measured Pages"}
        </button>
        <button
          type="button"
          className={interiorViewMode === "section" ? "ns-button ns-button-primary" : "ns-button ns-button-outline"}
          data-novel-proof-page-flow-select="section"
          onClick={() => setInteriorViewMode("section")}
        >
          {isFR ? "Section continue" : "Continuous Section"}
        </button>
        <span className="ns-card-meta">
          {trim.measurable
            ? (isFR
                ? "Pagination de prévisualisation mesurée dans le navigateur; l'export imprimable reste à valider."
                : "Browser-measured page projection; final print-file pagination is not yet verified.")
            : (isFR
                ? "Choisissez un format valide dans Préparer le livre pour activer les pages mesurées."
                : "Choose a valid trim and orientation in Book Setup to enable measured pages.")}
        </span>
      </div>
      <section data-novel-v3c-browser-page-evidence="provisional-only"
        className="ns-card" style={{ padding: 16, margin: "0 0 16px",
          background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 14 }}>
        <h3 style={{ margin: "0 0 8px", color: "#334155" }}>
          {isFR ? "V3C1 · Mesures de pages par section (navigateur uniquement)"
            : "V3C1 · Section Page Measurements (Browser Only)"}
        </h3>
        <p className="ns-card-meta" style={{ margin: "0 0 10px" }}>
          {isFR
            ? "Les comptes proviennent de la mise en colonnes réellement mesurée dans l'épreuve privée de Textbook. Ouvrez chaque section sous « Pages mesurées » ; les chiffres peuvent changer lorsque les images et les polices se stabilisent."
            : "Counts come from the existing Textbook proof's measured browser columns. Visit each saved section in Measured Pages mode; values may update as fonts and images settle."}
        </p>
        <p style={{ margin: "0 0 8px" }}
          data-novel-v3c-browser-observed-count={browserEvidence.observedSectionCount}>
          <strong>{isFR ? "Sections observées" : "Sections observed"}:</strong>{" "}
          {browserEvidence.observedSectionCount}/{browserEvidence.sectionCount}
          {browserEvidence.pendingSectionCount > 0
            ? " · " + browserEvidence.pendingSectionCount + " " +
              (isFR ? "en attente" : "awaiting browser measurement") : ""}
        </p>
        {browserEvidence.allSectionsObserved ? (
          <p style={{ margin: "0 0 8px" }} data-novel-v3c-browser-projected-total="provisional">
            <strong>{isFR ? "Total des pages projetées à l'écran" : "Provisional browser-projected page total"}:</strong>{" "}
            {browserEvidence.projectedBrowserPageCount}
          </p>
        ) : (
          <p className="ns-card-meta" style={{ margin: "0 0 8px" }}>
            {isFR ? "Aucun total n'est établi tant que toutes les sections ne sont pas observées."
              : "No all-section browser total is shown until every saved section has been observed."}
          </p>
        )}
        <details data-novel-v3c-browser-section-spans="true">
          <summary style={{ cursor: "pointer", fontWeight: 700 }}>
            {isFR ? "Inspecter les mesures provisoires" : "Inspect Provisional Section Measurements"}
          </summary>
          <div style={{ marginTop: 9, display: "grid", gap: 8 }}>
            {browserEvidence.units.map((unit) => (
              <div key={unit.key || unit.position} className="ns-card-meta"
                style={{ paddingBottom: 7, borderBottom: "1px solid #e2e8f0" }}>
                <strong>{unit.position}. {unit.title || (isFR ? "Sans titre" : "Untitled")}</strong>
                {" · "}{unit.browserPageCount == null
                  ? (isFR ? "en attente" : "not yet observed")
                  : unit.browserPageCount + " " + (isFR ? "page(s) navigateur" : "browser page(s)")}
                {unit.projectedStart != null
                  ? " · " + (isFR ? "plage provisoire" : "provisional span") +
                    " " + unit.projectedStart + "–" + unit.projectedEnd
                  : ""}
              </div>
            ))}
          </div>
        </details>
        <p style={{ margin: "12px 0 0", fontWeight: 700, color: "#92400e" }}
          data-novel-v3c-production-authority="not-enabled">
          {isFR
            ? "Ceci n'est pas une pagination physique, un PDF validé, un compte final de pages ni une autorisation de publier."
            : "This is not physical print pagination, an approved PDF, a final page count or publication authority."}
        </p>
      </section>
      <CreativePublicationProofViewer
        lang={isFR ? "fr" : "en"}
        title={clean(title) || (isFR ? "Aperçu de mise en page" : "Book Layout Preview")}
        format={format}
        cover={{ frontCoverUrl, backCoverUrl }}
        interiorItems={interiorItems}
        issues={issues}
        formatIssue={formatIssue}
        renderCover={renderCover}
        renderInteriorItem={renderInteriorItem}
        onReturn={onReturn}
        returnLabel={returnToPreparation
          ? (isFR ? "← Retour à la préparation de la publication" : "← Back to Publication Preparation")
          : (isFR ? "← Retour à Réviser votre livre" : "← Back to Review")}
        coverContentGeometry
        directInteriorNavigation
        stickyInteriorNavigation
      />
    </section>
  );
}
